import * as React from 'react';
import * as $ from 'jquery';
import * as _ from 'underscore';

import { IGlob } from '../../../../pdc-platform/src/ts/Glob';
import { ParameterField } from '../../../../_common/platform/tsx/ParameterField';
import TreeSelect from '../../../../_common/platform/tsx/TreeSelect';
import SelectField from '../../../../_common/platform/tsx/SelectField';

import RadioGroupFieldComponent from '../../../../_common/platform/tsx/RadioGroupField'; //LL: 16.01.25
import NumberField from '../../../../_common/platform/tsx/NumberField'; //LL: 16.01.25

import { IAttributeTreePropertiesBase } from '../../../../_common/platform/tsx/filtering/Interfaces';

import { normalizeTreeOptionsValue } from '../../../../_common/platform/tsx/filtering/FilterUtilities';

import { IDisplayValue } from '../../../../_common/platform/ts/Interfaces';

import { ATTRIBUTE_SEPARATOR } from '../../../../pdc-platform/src/ts/stores/EntityModelsStore';

import { IValuesCollection } from '../../../platform/ts/appTheme';
import GroupingParameterField from '../GroupingParameterField';
import { ATTRIBUTE_TYPES } from '../../ts/ConfigurationConstants';

const DEFAULT_NODE_TITLE = '<Default>';

export interface ITreeOptionsProperties {
    attribute: string;
    value?: IAttributeTreePropertiesBase;
    dataSourceID: number;
    theme: IValuesCollection;
    glob: IGlob;
    hideOptionalFields?: boolean;
    showNotUsed?: boolean;
    allowParentNotUsed?: boolean; //LL: 10.03.25 default false, for TreeList

    getTreeEntityValues?(dataSourceID: number, attribute: string, parentAttribute: string, labelAttribute: string);
    onChange(value: IAttributeTreePropertiesBase): void;
}

function getValidAttributes(
    attribute: string,
    validTypes: string[],
    dataSourceID: number,
    glob: IGlob
): {
    attributes: string[];
    defaultAttribute: string;
} {
    const entitiesHash = glob.EntityModelsStore.getEntitiesHashByDS(dataSourceID);

    if (entitiesHash == null || attribute == null) {
        return null;
    }

    const referredEntity = glob.EntityModelsStore.getReferredEntity(attribute, dataSourceID);

    if (referredEntity == null) {
        return null;
    }

    const referredAttribute = referredEntity.id;
    const keyAttribute = referredAttribute + '.' + referredEntity.original.keyAttribute;
    const selfRefferers: string[] = [];
    const others: string[] = [];

    _.each(entitiesHash, (value, id) => {
        if (
            id != referredAttribute &&
            id !== keyAttribute &&
            id.indexOf(referredAttribute + ATTRIBUTE_SEPARATOR) === 0
        ) {
            const title = id.substr(referredAttribute.length + 1, id.length);

            if (title !== '') {
                const isSelfReferrer = value.original.refer === referredAttribute;

                if (isSelfReferrer) {
                    if (title.toLowerCase() === 'parent') {
                        selfRefferers.unshift(title);
                    } else {
                        selfRefferers.push(title);
                    }
                } else {
                    const attributeType = value.original.type;

                    if (validTypes.indexOf(attributeType) > -1) {
                        others.push(title);
                    }
                }
            }
        }
    });

    let defaultAttribute: string;

    if (selfRefferers.length > 0) {
        defaultAttribute = selfRefferers[0];
    }

    return {
        attributes: selfRefferers.concat(others),
        defaultAttribute
    };
}

export default function TreeOptions(props: ITreeOptionsProperties) {
    function processValueChange(value: IAttributeTreePropertiesBase) {
        if (typeof props.onChange === 'function') {
            normalizeTreeOptionsValue(value);
            props.onChange(value);
        }
    }

    const handleOnParentAttributeChange = (value: string) => {
        const valueCopy = props.value != null ? { ...props.value } : {};

        valueCopy.parentAttribute = value;
        processValueChange(valueCopy);
    };

    const handleOnRootNodeChange = (value: IDisplayValue[]) => {
        const valueCopy = props.value != null ? { ...props.value } : {};

        valueCopy.rootNode = value != null ? value[0] : null;
        processValueChange(valueCopy);
    };

    const handleOnNotUsedNodeChange = (value: IDisplayValue[]) => {
        const valueCopy = props.value != null ? { ...props.value } : {};

        valueCopy.notUsedNode = value != null ? value[0] : null;
        processValueChange(valueCopy);
    };

    //LL: 17.01.25
    const handleOnNotUsedNodeIdChange = (value: number) => {
        const valueCopy = props.value != null ? { ...props.value } : {};

        valueCopy.notUsedNodeId = value;
        processValueChange(valueCopy);
    };

    //LL: 17.01.25
    const handleOnNotUsedNodeMethodChange = (value: string) => {
        const valueCopy = props.value != null ? { ...props.value } : {};

        valueCopy.notUsedNode_defBySelect = value === 'selectItem';
        processValueChange(valueCopy);
    };

    const handleOnLabelAttributeChange = (value: string) => {
        const valueCopy = props.value != null ? { ...props.value } : {};

        valueCopy.labelAttribute = value;
        processValueChange(valueCopy);
    };

    function getAttributeOptions(selectedAttribute: string, validTypes: string[]) {
        let { attributes, defaultAttribute } =
            getValidAttributes(props.attribute, validTypes, props.dataSourceID, props.glob) || {};

        if (selectedAttribute != null && (attributes == null || attributes.indexOf(selectedAttribute) < 0)) {
            if (attributes == null) {
                attributes = [];
            }

            //Even if assigned Attribute is not valid now, show it to make the assignment visible.
            //Normally, not valid selections must be dropped by normalization.
            attributes.push(selectedAttribute);
        }

        const options = _.map(attributes, (attribute, i) => {
            return (
                <option key={i} value={attribute}>
                    {attribute}
                </option>
            );
        });

        return {
            options,
            defaultAttribute
        };
    }

    const value = props.value || {};

    let optionalFields: React.JSX.Element[];

    if (props.hideOptionalFields !== true) {
        const labelAttributeOptions = getAttributeOptions(value.labelAttribute, [
            ATTRIBUTE_TYPES.INTEGER,
            ATTRIBUTE_TYPES.STRING
        ]);

        optionalFields = [
            <ParameterField key="label-attribute" title={"Label Attribute (is used to define tree node's title)"}>
                <SelectField
                    value={value.labelAttribute}
                    allowNull={true}
                    nullOptionTitle={DEFAULT_NODE_TITLE}
                    onChange={handleOnLabelAttributeChange}
                >
                    {labelAttributeOptions.options}
                </SelectField>
            </ParameterField>,

            <ParameterField key="root-node" title={'Root Node'}>
                <TreeSelect
                    attributeData={{
                        attribute: props.attribute
                    }}
                    dataSourceID={props.dataSourceID}
                    value={value.rootNode}
                    allowNull={true}
                    theme={props.theme}
                    getValues={props.getTreeEntityValues(
                        props.dataSourceID,
                        props.attribute,
                        value.parentAttribute || labelAttributeOptions.defaultAttribute,
                        value.labelAttribute
                    )}
                    glob={props.glob}
                    onChange={handleOnRootNodeChange}
                />
            </ParameterField>
        ];

        if (props.showNotUsed) {
            // optionalFields.push(
            //     <ParameterField key="not-used-node" title={'Not Used Node'}>
            //         <TreeSelect
            //             attributeData={{
            //                 attribute: props.attribute
            //             }}
            //             dataSourceID={props.dataSourceID}
            //             value={value.notUsedNode}
            //             allowNull={true}
            //             theme={props.theme}
            //             getValues={props.getTreeEntityValues(
            //                 props.dataSourceID,
            //                 props.attribute,
            //                 value.parentAttribute || labelAttributeOptions.defaultAttribute,
            //                 value.labelAttribute
            //             )}
            //             glob={props.glob}
            //             onChange={handleOnNotUsedNodeChange}
            //         />
            //     </ParameterField>
            // );

            //LL: 16.01.25: RadioGroup
            let rgItems: any = [];

            // let curMethod = 'selectItem';
            let curMethod = value.notUsedNode_defBySelect === false ? 'defineId' : 'selectItem';

            let disabledSelectItem = curMethod !== 'selectItem';
            //let disabledSelectItem = true; // try
            let disabledDefineId = curMethod !== 'defineId';

            //LL: content only for 'vertical' !!!

            let styleContent: React.CSSProperties = { paddingTop: '6px', paddingBottom: '6px' };

            //LL: sample, how to disable div:
            // https://stackoverflow.com/questions/51259528/how-to-totally-disable-a-react-component
            // const MyComponent = ({disabled}) => {
            //     return (
            //         <div style={disabled ? {pointerEvents: "none", opacity: "0.4"} : {}}>
            //             <h1> Text</h1>
            //             <input type="text"/>
            //             <input type="password"/>
            //             <button>Login</button>
            //         </div>
            //     )
            // }
            let styleContent_disabled: React.CSSProperties = {
                paddingTop: '6px',
                paddingBottom: '6px',
                pointerEvents: 'none',
                opacity: '0.4'
            };

            rgItems.push({
                text: 'Select Item',
                value: 'selectItem',
                content: (
                    <div style={!disabledSelectItem ? styleContent : styleContent_disabled}>
                        <TreeSelect
                            attributeData={{
                                attribute: props.attribute
                            }}
                            dataSourceID={props.dataSourceID}
                            value={value.notUsedNode}
                            allowNull={true}
                            theme={props.theme}
                            getValues={props.getTreeEntityValues(
                                props.dataSourceID,
                                props.attribute,
                                value.parentAttribute || labelAttributeOptions.defaultAttribute,
                                value.labelAttribute
                            )}
                            glob={props.glob}
                            onChange={handleOnNotUsedNodeChange}
                        />
                    </div>
                )
            });

            // let curNotUsedId = value.notUsedNodeId; // 0;
            let curNotUsedId = value.notUsedNodeId || 0;
            rgItems.push({
                text: 'Determine the ID for the artificial "Not Used" node', //'Define Id',
                value: 'defineId',
                //disabled: true, //LL: temp
                content: (
                    <div style={styleContent}>
                        <NumberField
                            readOnly={disabledDefineId}
                            value={curNotUsedId}
                            // allowNull={true}
                            allowNull={false}
                            applyOnEnter={true}
                            integer={true}
                            onChange={handleOnNotUsedNodeIdChange}
                        />
                    </div>
                )
            });

            let rgOptions = {
                name: 'RG_setNotUsedItem', //LL: needed to differ RadioGroup from other RadioGroups
                layout: 'vertical',
                // layout: 'horizontal',
                //horizontalOverflow: 'hidden', // HorizontalOverflowType;  // 'hidden' | 'wrap' (default)
                items: rgItems
            };

            //let styleScaleMethod: React.CSSProperties = { marginTop: '5px', marginBottom: '5px' }; // ??
            // let elemRG = (
            //     <div style={styleScaleMethod}>
            //         <RadioGroupFieldComponent
            //             value={curMethod}
            //             options={rgOptions}
            //             onChange={null}
            //         />
            //     </div>
            // );
            let elemRG = (
                <RadioGroupFieldComponent
                    value={curMethod}
                    options={rgOptions}
                    onChange={handleOnNotUsedNodeMethodChange}
                />
            );
            optionalFields.push(
                <ParameterField key="not-used-node" title={'Not Used Node'}>
                    {elemRG}
                </ParameterField>
            );
        }
    }

    let title: string;
    let nullOptionTitle: string;

    const parentAttributeOptions = getAttributeOptions(value.parentAttribute, [ATTRIBUTE_TYPES.INTEGER]);

    if (parentAttributeOptions.defaultAttribute != null) {
        title = 'Parent Attribute (is used to build a tree)';
        nullOptionTitle = DEFAULT_NODE_TITLE + ' - ' + parentAttributeOptions.defaultAttribute;
        //LL: 10.03.25 default false, for TreeList:
        if (props.allowParentNotUsed === true) {
            nullOptionTitle = '<Not Used>';
        }
    } else {
        title = 'Parent Attribute (No Attribute suitable for building a tree is found)';
        nullOptionTitle = '<Not specified>';
    }

    return (
        <div className="tree-options">
            <GroupingParameterField title={'How to show tree'}>
                <ParameterField title={title}>
                    <SelectField
                        value={value.parentAttribute}
                        allowNull={true}
                        nullOptionTitle={nullOptionTitle}
                        onChange={handleOnParentAttributeChange}
                    >
                        {parentAttributeOptions.options}
                    </SelectField>
                </ParameterField>

                {optionalFields}
            </GroupingParameterField>
        </div>
    );
}
